import { ReactElement } from "react";
import { AddOnProductType, AddOnsByType, IAddon } from "@stores/addons/types";
import { IConfig } from "@stores/config/types";
import {
  IChangePlanData,
  ICreatePurchaseAddOn,
  IPurchase,
} from "@stores/purchases/types";
import { AddonSubscribedProductType } from "@stores/subscriptions/types";

type ByProductType<T> = Partial<Record<AddOnProductType, T>>;

export type AddOnConfigType = Pick<IConfig, "feature_flags">;

export type IRemoveAddOn = Pick<ICreatePurchaseAddOn, "product_type"> &
  Partial<Pick<ICreatePurchaseAddOn, "prepaid_units">>;

export interface IAddOnRequires {
  anyOf?: readonly [AddOnProductType, ...AddOnProductType[]];
}

export const anyOf = (
  types: readonly [AddOnProductType, ...AddOnProductType[]],
): IAddOnRequires => ({ anyOf: types });

/** what an upsert reads; a checkout has no purchase yet still builds one */
export interface IUpsertContext {
  config: AddOnConfigType;
  subscribedByType: ByProductType<AddonSubscribedProductType>;
  addOnsByType: AddOnsByType;
  getUpsertAddOnsFor(
    productType: AddOnProductType,
    units?: number,
  ): ICreatePurchaseAddOn[];
}

export interface IPurchaseContext extends IUpsertContext {
  purchase: IPurchase;
  isCloudHosted: boolean;
  isYearly: boolean;
  isTrial: boolean;
  canChangeSubscription: boolean;
  getRemoveAddOnsFor(productType: AddOnProductType): IRemoveAddOn[];
}

export interface IAddOnUsage {
  /** the quota row or meter has actually reported */
  hasQuota: boolean;
  usage: number;
  /**
   * the graph's ceiling. What it is depends on the product: prepaid rows for
   * dwh, the per-period included rows dwh-rent compares its footprint against,
   * the lifetime free pool (`free_units`) for pay-as-you-go add-ons
   */
  includedUnits: number;
}

/** a metered add-on also knows how `usage` splits between the allowance and the bill */
export interface IMeteredAddOnUsage extends IAddOnUsage {
  includedUnitsUsed: number;
  billableUnits: number;
  /**
   * nothing left to draw down: every further unit bills. Only pay-as-you-go
   * add-ons answer this; rent-metered ones have no pool to consume
   */
  areFreeUnitsConsumed?: boolean;
}

export interface IAddOnContext<D extends IAddOnDescriptor = IAddOnDescriptor>
  extends IPurchaseContext {
  productType: AddOnProductType;
  subscribed?: AddonSubscribedProductType;
  addOn?: IAddon;
  getEnableAddOnPayload: (...args: UpsertArgs<D>) => IChangePlanData;
  getDisableAddOnPayload(): IChangePlanData;
  getUsage: () => ReturnOf<D, "getUsage">;
  getPricePerUnit: () => ReturnOf<D, "getPricePerUnit">;
  getIncludedUnits: () => ReturnOf<D, "getIncludedUnits">;
  isVisible(): boolean;
  title: IAddOnDescriptor["title"];
  getEstimateRowItem(units: number): IAddOnRowItem | undefined;
  ManageCard?: AddOnSlot;
}

export interface IAddOnSlotProps<
  D extends IAddOnDescriptor = IAddOnDescriptor,
> {
  addOn: IAddOnContext<D>;
}

type SlotFor<D extends IAddOnDescriptor> = (
  props: IAddOnSlotProps<D>,
) => ReactElement | null;

/** a slot as the manage page reads it, its kind already erased */
export type AddOnSlot = SlotFor<IAddOnDescriptor>;

/**
 * One add-on's row on the plan estimate, the billing page or the instance
 * invoice, under the descriptor's title: "Storage (500k stored rows)  $40.00/month".
 * `price` absent when there is no fixed price to show.
 */
export interface IAddOnRowItem {
  detail?: string;
  price?: string;
}

/** the checkout total sums its rows, so the price stays a number */
export interface ICheckoutRowItem {
  detail?: string;
  price: number;
}

/** a row as the registry hands it to a page: the descriptor's identity and title added */
export type IAddOnRow = IAddOnRowItem & {
  productType: AddOnProductType;
  title: string;
};

export type ICheckoutRow = ICheckoutRowItem & {
  productType: AddOnProductType;
  title: string;
};

/** an add-on as a checkout reads it: no purchase yet, only its catalogue row */
export interface ICheckoutAddOnContext extends IUpsertContext {
  productType: AddOnProductType;
  addOn: IAddon;
  title: string;
}

interface IAddOnDescriptorBase {
  productType: AddOnProductType;
  /** the add-on's display name, the one source every slot's label reads */
  title: string;
  isVisible?(ctx: IAddOnContext): boolean;
  requires?: IAddOnRequires;

  /** absent means the add-on never shows on the plan estimate */
  getEstimateRowItem?(ctx: IAddOnContext, units: number): IAddOnRowItem;
  /** the add-on's billing-page row, only ever asked while subscribed; absent means no row */
  getBillingRowItem?(ctx: IAddOnContext): IAddOnRowItem;
  /** the add-on's checkout row, only ever asked with a catalogue row; absent means never quoted */
  getCheckoutRowItem?(
    ctx: ICheckoutAddOnContext,
    units: number,
  ): ICheckoutRowItem;
  /** the add-on's row on the instance card's invoice; absent or undefined means no row */
  getPurchaseLineItem?(ctx: IAddOnContext): IAddOnRowItem | undefined;
}

/** harbormaster `meter_strategy` NULL; the customer picks `prepaid_units` */
export interface IPrepaidAddOnDescriptor extends IAddOnDescriptorBase {
  kind: "prepaid";
  ManageCard?: SlotFor<IPrepaidAddOnDescriptor>;
  getUsage?(ctx: IAddOnContext): IAddOnUsage | undefined;
  getPricePerUnit?: never;
  getIncludedUnits?: never;
  getUpsertAddOns?(ctx: IUpsertContext, units: number): ICreatePurchaseAddOn[];
}

/** harbormaster `meter_strategy` pay_as_you_go or rent; Stripe rejects a quantity */
export interface IMeteredAddOnDescriptor extends IAddOnDescriptorBase {
  kind: "metered";
  ManageCard?: SlotFor<IMeteredAddOnDescriptor>;
  getUsage(ctx: IAddOnContext): IMeteredAddOnUsage | undefined;
  getPricePerUnit(ctx: IAddOnContext): number;
  /** the free allowance, falling back to the published one before subscribing */
  getIncludedUnits(ctx: IAddOnContext): number;
  getUpsertAddOns?(ctx: IUpsertContext): ICreatePurchaseAddOn[];
}

/** harbormaster `meter_strategy` NULL with a fixed quantity; nothing to meter */
export interface IFlatAddOnDescriptor extends IAddOnDescriptorBase {
  kind: "flat";
  ManageCard?: SlotFor<IFlatAddOnDescriptor>;
  getUsage?: never;
  getPricePerUnit?: never;
  getIncludedUnits?: never;
  getUpsertAddOns?(ctx: IUpsertContext): ICreatePurchaseAddOn[];
}

export type IAddOnDescriptor =
  | IPrepaidAddOnDescriptor
  | IMeteredAddOnDescriptor
  | IFlatAddOnDescriptor;

/** the interface a concrete descriptor was declared against, by its kind */
type DescriptorOfKind<K> = Extract<IAddOnDescriptor, { kind: K }>;

// `D` is the naked check type on purpose: it keeps the constraint resolvable
// inside the generic builder and hook, which is what lets them cast
type UpsertArgs<D extends IAddOnDescriptor> = D extends { kind: infer K }
  ? Parameters<NonNullable<DescriptorOfKind<K>["getUpsertAddOns"]>> extends [
      unknown,
      ...infer Rest,
    ]
    ? Rest
    : never
  : never;

// a descriptor that defines the method gets its exact return; the bare union
// keeps `| undefined` since the method may be missing
type ReturnOf<
  D extends IAddOnDescriptor,
  M extends "getUsage" | "getPricePerUnit" | "getIncludedUnits",
> = D extends { [P in M]: (...args: never[]) => infer R }
  ? R
  : ReturnType<NonNullable<IAddOnDescriptor[M]>> | undefined;
